/*
  PROMO STRIP — shows and hides each promo at its exact start and end,
  runs the countdown, keeps the sideways track in panel order, and copies
  the code. Markup and first paint: snippets/bc-promo-strip.liquid.

  data-start / data-end are epoch SECONDS written by Liquid from the stored
  GMT instants, so this compares them straight against the browser clock —
  no timezone handling, which is why BST cannot put the two out of step.

  Liquid paints the first state; from then on this file is the authority,
  because Shopify can serve a cached page after a promo has started or ended.

  The track holds every promo three times (data-copy 1–3) so the CSS scroll
  can loop. Exactly one live promo makes the track --static — centred and
  still on desktop; anything else scrolls. That is the old strip's rule.

  ⚠ THE COUNTDOWN IS PINNED TO ITS WORST-CASE WIDTH. Barlow has no
  fixed-width figures, so a ticking countdown changes the text width, which
  changed the track's width with it — that is what made the scroll nudge
  every second. Boxing each number fixed that but left uneven gaps between
  the numbers and their labels, so instead pinTimerWidths() measures the
  countdown with every digit pair and pins it to the widest. The numbers sit
  against their labels exactly as the old strip's do, nothing after the
  countdown moves, and the leftover slack sits inside the countdown after
  SECS, where there is nothing to see.

  ⚠ THE SCROLL LOOPS ON A MEASURED DISTANCE, NOT A PERCENTAGE. The old strip
  moved by 50% of its track, which is one copy plus HALF the gap, so every
  loop landed ~21px out of step — a visible jump, measured on the old strip
  too (15 Sep). Here measureScroll() measures ONE copy (the distance from the
  first item of copy 1 to the first item of copy 2, gap included) and sets
  --bc-promo-shift to it, with the duration derived from
  --bc-promo-scroll-speed so the speed is unchanged. Re-measured on resize
  and whenever the live promos change, because both change the width.

  No dependency on theme.js — loaded with defer from the snippet.
*/
(function () {
  'use strict';

  var COPIED_MS = 2000;
  var STATIC_CLASS = 'bc-promo-strip__track--static';
  var RESIZE_MS = 200;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function nowSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  function initStrip(strip) {
    if (strip.dataset.promoReady === 'true') return;
    strip.dataset.promoReady = 'true';

    var track = strip.querySelector('[data-promo-track]');
    if (!track) return;

    var all = Array.prototype.slice.call(track.querySelectorAll('[data-promo-item]'));

    // Panel order within each copy; entries without an order keep their Liquid order.
    var ordered = [];
    ['1', '2', '3'].forEach(function (copy) {
      all.filter(function (item) { return item.dataset.copy === copy; })
        .map(function (item, index) { return { item: item, index: index }; })
        .sort(function (a, b) {
          return (Number(a.item.dataset.order) - Number(b.item.dataset.order)) || (a.index - b.index);
        })
        .forEach(function (entry) { ordered.push(entry.item); });
    });
    ordered.forEach(function (item) { track.appendChild(item); });

    var firsts = ordered.filter(function (item) { return item.dataset.copy === '1'; });

    // One copy's width, gap included: the distance between the same item in copy 1 and copy 2.
    function copyWidth() {
      var visible = ordered.filter(function (item) { return !item.hidden && item.offsetParent !== null; });
      var first = visible[0];
      if (!first) return 0;
      var next = visible.find(function (item) {
        return item.dataset.promoId === first.dataset.promoId && item.dataset.copy !== first.dataset.copy;
      });
      return next ? Math.round(next.offsetLeft - first.offsetLeft) : 0;
    }

    var DIGIT_KEYS = ['days', 'hours', 'mins', 'secs'];

    // Pin each countdown to the width it would have with the widest digits, so
    // a tick can never change the track's width or move anything after it.
    // Runs synchronously, so nothing is painted mid-measurement.
    function pinTimerWidths() {
      firsts.forEach(function (first) {
        var timer = first.querySelector('[data-promo-timer]');
        if (!timer) return;
        var spans = DIGIT_KEYS.map(function (k) { return timer.querySelector('[data-promo-' + k + ']'); }).filter(Boolean);
        if (!spans.length) return;

        var timers = ordered
          .filter(function (item) { return item.dataset.promoId === first.dataset.promoId; })
          .map(function (item) { return item.querySelector('[data-promo-timer]'); })
          .filter(Boolean);
        timers.forEach(function (el) { el.style.minWidth = ''; });

        // Every digit pair, measured: the widest single digit does not always
        // give the widest pair once letter spacing is in play, and a pin even
        // a pixel short lets the text overflow and the width move again.
        var saved = spans.map(function (span) { return span.textContent; });
        var worst = 0;
        for (var d = 0; d <= 9; d++) {
          var pair = String(d) + String(d);
          spans.forEach(function (span) { span.textContent = pair; });
          worst = Math.max(worst, timer.getBoundingClientRect().width);
        }
        spans.forEach(function (span, i) { span.textContent = saved[i]; });

        if (worst > 0) {
          var pinned = Math.ceil(worst) + 1;
          timers.forEach(function (el) { el.style.minWidth = pinned + 'px'; });
        }
      });
    }

    function measureScroll() {
      pinTimerWidths();
      if (track.classList.contains(STATIC_CLASS) && window.matchMedia('(min-width: 769px)').matches) return;
      var unit = copyWidth();
      if (!unit) return;
      // A wide screen with short promos needs more copies, or the end of the
      // loop would show empty space on the right.
      var guard = 0;
      while (track.scrollWidth - unit < window.innerWidth && guard < 6) {
        var lastCopy = String(Math.max.apply(null, ordered.map(function (item) { return Number(item.dataset.copy); })) + 1);
        firsts.forEach(function (item) {
          var clone = item.cloneNode(true);
          clone.dataset.copy = lastCopy;
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[data-promo-copy]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
          clone.hidden = item.hidden;
          track.appendChild(clone);
          ordered.push(clone);
        });
        guard += 1;
      }
      var speed = parseFloat(getComputedStyle(track).getPropertyValue('--bc-promo-scroll-speed')) || 40;
      track.style.setProperty('--bc-promo-shift', unit + 'px');
      track.style.animationDuration = (unit / speed).toFixed(2) + 's';
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(measureScroll, RESIZE_MS);
    });

    function tick() {
      var now = nowSeconds();
      var live = firsts.filter(function (item) {
        return now >= Number(item.dataset.start) && now < Number(item.dataset.end);
      });
      var liveIds = live.map(function (item) { return item.dataset.promoId; });

      ordered.forEach(function (item) {
        var hide = liveIds.indexOf(item.dataset.promoId) === -1;
        if (item.hidden !== hide) item.hidden = hide;
      });

      if (!live.length) {
        if (!strip.hidden) strip.hidden = true;
        strip.dataset.promoLive = 'false';
        return;
      }

      if (strip.hidden) strip.hidden = false;
      strip.dataset.promoLive = 'true';
      var liveKey = liveIds.join('|');
      var changed = liveKey !== strip.dataset.promoLiveSet;
      strip.dataset.promoLiveSet = liveKey;
      track.classList.toggle(STATIC_CLASS, live.length === 1);
      // data-promo-bg, never data-bg: kubix.js JSON.parses every [data-bg] on the page.
      strip.style.setProperty('--bc-promo-bg', live[0].dataset.promoBg);

      if (changed) measureScroll();

      var left, timers;
      live.forEach(function (first) {
        left = Math.max(0, Number(first.dataset.end) - now);
        timers = track.querySelectorAll('[data-promo-id="' + first.dataset.promoId + '"] [data-promo-timer]');
        Array.prototype.forEach.call(timers, function (timer) {
          timer.querySelector('[data-promo-days]').textContent = pad(Math.floor(left / 86400));
          timer.querySelector('[data-promo-hours]').textContent = pad(Math.floor((left % 86400) / 3600));
          timer.querySelector('[data-promo-mins]').textContent = pad(Math.floor((left % 3600) / 60));
          timer.querySelector('[data-promo-secs]').textContent = pad(left % 60);
        });
      });
    }

    tick();
    window.setInterval(tick, 1000);
  }

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        if (document.execCommand('copy')) resolve();
        else reject(new Error('copy refused'));
      } catch (error) {
        reject(error);
      }
      document.body.removeChild(area);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-promo-copy]');
    if (!button) return;

    event.preventDefault();
    var strip = button.closest('[data-promo-strip]');
    var announce = strip && strip.querySelector('[data-promo-announce]');

    copyText(button.dataset.promoCopy).then(function () {
      button.classList.add('is-copied');
      if (announce) announce.textContent = 'Code copied';
      window.setTimeout(function () {
        button.classList.remove('is-copied');
        if (announce) announce.textContent = '';
      }, COPIED_MS);
    }).catch(function () {});
  });

  // With a link the code is a span role="button" (as on the old strip), so give it the keys a button has.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var span = event.target.closest && event.target.closest('span[data-promo-copy]');
    if (!span) return;
    event.preventDefault();
    span.click();
  });

  function init() {
    document.querySelectorAll('[data-promo-strip]').forEach(initStrip);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
